/**
 * Router Guards — Donor Portal
 *
 * Note: Role guard is not added yet. Auth store will not be effective as of now.
 */
import { START_LOCATION, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores';

export function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  // Direct entry to /login (e.g. an invite email link opened on a shared
  // device) while a session already exists — send the user to a dedicated
  // page (ActiveSessionView) that signs the previous donor out automatically
  // and then moves on to the login form, instead of silently redirecting into
  // the previous donor's portal. `from === START_LOCATION` is only true on the
  // first navigation after a page load, which distinguishes this from
  // in-app navigation (e.g. the browser Back button after signing in) —
  // that case must keep the "redirect home" behavior below.
  // Match on the route NAME, not the literal path: Vue Router's default non-strict,
  // case-insensitive matching resolves '/login/' and '/LOGIN' to the login route
  // while leaving `to.path` as typed, so a path comparison would let those
  // variants render the login form over the existing session.
  if (to.name === 'login' && from === START_LOCATION && authStore.isAuthenticated) {
    next({ name: 'active-session', query: to.query });
    return;
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if (requiresAuth && !authStore.isAuthenticated) {
    // Use to.path — fullPath is excluded to prevent tokens, IDs, or PII in
    // query strings from leaking into browser history and server access logs.
    next({
      path: '/login',
      query: { redirect: to.path },
    });
  } else if (
    authStore.isAuthenticated &&
    authStore.mfaSetupRequired &&
    to.path !== '/mfa-setup' &&
    // The redirect above lands here; without this exemption a session with a
    // pending MFA setup would be bounced straight into that donor's MFA flow,
    // skipping the sign-out-or-continue choice.
    to.name !== 'active-session'
  ) {
    // Block access until first-time MFA setup is complete
    next({ path: '/mfa-setup', replace: true });
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ path: '/' });
  } else {
    next();
  }
}

/**
 * Role Guard — not implemented yet.
 */
export function roleGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  next();
}

export function logoutGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  if (to.path === '/logout') {
    const authStore = useAuthStore();
    authStore.clearAuth();
    next({ path: '/login' });
  } else {
    next();
  }
}
